import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button, Card, Empty, PageTitle } from "@/components/ui";
import { ResumeRow } from "@/components/resume";
import { Accent, Deadline, StatePill, Stepper } from "@/components/pipeline";
import { useMe } from "@/lib/session";
import { applicationsQuery, type ApplicationItem } from "@/lib/applications";
import { VERTICALS } from "@postman/core/verticals";

export const Route = createFileRoute("/_auth/_onboarded/dashboard")({
  loader: ({ context }) => context.queryClient.ensureQueryData(applicationsQuery),
  component: Dashboard,
});

function Dashboard() {
  const me = useMe();
  const profile = me.profile!;

  return (
    <div className="flex flex-col gap-12">
      <PageTitle
        eyebrow={me.cycle ? me.cycle.name : "No cycle open"}
        title={`Hello, ${me.user.name.split(" ")[0]}.`}
        lede={
          me.cycle
            ? `${VERTICALS.length} verticals are recruiting. Your profile goes with every application you send.`
            : "Recruitment is closed at the moment. Your profile is saved and will be waiting when the next cycle opens."
        }
      />

      <section className="flex flex-col gap-5">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="mono text-ink-3">Your profile</h2>
          <Link
            to="/onboarding"
            className="mono underline underline-offset-4 hover:text-[color:var(--accent)]"
          >
            Edit
          </Link>
        </div>

        <Card className="flex flex-col gap-7">
          <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-3">
            <Row label="BITS ID" value={me.user.bitsId ?? "—"} />
            <Row label="Branch" value={profile.branch ?? "—"} />
            <Row label="Phone" value={profile.phone ?? "—"} />
          </dl>

          {profile.about ? (
            <p className="max-w-[62ch] text-[15px] leading-relaxed text-ink-2">
              {profile.about}
            </p>
          ) : null}

          {profile.links.length > 0 ? (
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {profile.links.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer nofollow"
                    className="mono underline underline-offset-4 hover:text-[color:var(--accent)]"
                  >
                    {link.kind}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}

          <ResumeRow resume={profile.resume} />
        </Card>
      </section>

      <Applications />
    </div>
  );
}

/**
 * Every application, with the round it is sitting in.
 *
 * The stepper is the whole point of this screen: "submitted" is not a status
 * anybody can act on, and "waiting on you / waiting on us" is.
 */
function Applications() {
  const { data } = useSuspenseQuery(applicationsQuery);
  const mine = data.items.filter((i) => i.application);
  const openNow = data.items.filter((i) => i.open && !i.application).length;

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="mono text-ink-3">Your applications</h2>
        {openNow > 0 ? (
          <Link
            to="/apply"
            className="mono underline underline-offset-4 hover:text-[color:var(--accent-text)]"
          >
            {openNow} more open
          </Link>
        ) : null}
      </div>

      {mine.length === 0 ? (
        <div className="flex flex-col items-start gap-5">
          <Empty>
            {data.cycle
              ? "Nothing sent yet. Pick a vertical and tell them what you have built."
              : "Applications open with the cycle. Nothing yet."}
          </Empty>
          {data.cycle ? (
            <Link to="/apply">
              <Button>Browse the verticals</Button>
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {mine.map((item) => (
            <ApplicationCard key={item.slug} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function ApplicationCard({ item }: { item: ApplicationItem }) {
  const app = item.application!;
  const current = app.rounds.find((r) => r.id === app.currentRoundId) ?? null;

  return (
    <Accent slug={item.slug}>
      <Card className="flex flex-col gap-7">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/apply/$vertical"
            params={{ vertical: item.slug }}
            className="display text-[22px] underline-offset-[6px] hover:underline"
          >
            {item.name}
          </Link>
          <div className="flex items-center gap-3">
            {current ? <Deadline at={current.closesAt} late={current.late} /> : null}
            <StatePill state={app.state} />
          </div>
        </div>

        <Stepper rounds={app.rounds} state={app.state} />
      </Card>
    </Accent>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <dt className="mono text-ink-3">{label}</dt>
      <dd className="text-[15px]">{value}</dd>
    </div>
  );
}
